import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Printer, Share2, Loader2 } from 'lucide-react'
import { toPng } from 'html-to-image'
import { Alert, AlertDescription, Button, Input, Label } from '../ui'
import { useOrganization } from '../../contexts/OrganizationContext'
import { getProgramaWithSeccionesYHabilidades } from '../../lib/supabase'
import { getSeccionTipoLabel } from '../../constants/seccionTipos'
import { buildTimetable, formatDuracionTotal } from './timetableHelpers'

const ProgramaImprimibleView = () => {
  const { programaId } = useParams()
  const navigate = useNavigate()
  const { org, orgLogo } = useOrganization()

  const [programa, setPrograma] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [horaInicio, setHoraInicio] = useState('07:00')
  const [generando, setGenerando] = useState(false)
  const contentRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!programaId) return
      setLoading(true)
      const { data, error: err } = await getProgramaWithSeccionesYHabilidades(programaId)
      if (cancelled) return
      if (err) setError('No pudimos cargar el programa')
      else setPrograma(data)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [programaId])

  const timetable = useMemo(() => {
    if (!programa?.secciones) return []
    return buildTimetable(horaInicio, programa.secciones)
  }, [programa, horaInicio])

  const handleImprimir = () => {
    window.print()
  }

  // Genera PNG y comparte (Web Share API en mobile, descarga en desktop)
  const handleCompartir = async () => {
    if (!contentRef.current || generando) return
    setGenerando(true)
    setError(null)
    try {
      // pixelRatio=1 da ~300-500KB. Suficiente para WhatsApp/Telegram
      // que igual comprimen al subir. Mas alto solo aumenta tamano sin
      // beneficio visible una vez compartido.
      const dataUrl = await toPng(contentRef.current, {
        cacheBust: true,
        pixelRatio: 1,
        backgroundColor: '#000000',
        filter: (node) => !node.classList || !node.classList.contains('no-print')
      })

      const blob = await (await fetch(dataUrl)).blob()
      const slug = (programa.titulo || 'programa')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/gi, '_')
        .toLowerCase()
      const filename = `${slug || 'programa'}.png`
      const file = new File([blob], filename, { type: 'image/png' })

      const shareData = {
        files: [file],
        title: programa.titulo || 'Programa',
        text: `${programa.titulo || 'Programa'} · ${formatDuracionTotal(programa.secciones || [])}`
      }

      const canShareFiles =
        typeof navigator !== 'undefined' &&
        typeof navigator.share === 'function' &&
        typeof navigator.canShare === 'function' &&
        navigator.canShare(shareData)

      if (canShareFiles) {
        try {
          await navigator.share(shareData)
        } catch (err) {
          if (err.name !== 'AbortError') throw err
        }
      } else {
        // Fallback desktop: descargar el PNG
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }
    } catch (err) {
      console.error('Error generando imagen:', err)
      setError('No pudimos generar la imagen. Intenta de nuevo.')
    } finally {
      setGenerando(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error || !programa) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 gap-4">
        <Alert variant="destructive">
          <AlertDescription>{error || 'Programa no encontrado'}</AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver
        </Button>
      </div>
    )
  }

  const duracionTotal = formatDuracionTotal(programa.secciones || [])
  const tieneSecciones = (programa.secciones || []).length > 0
  const enfoque = (programa.habilidades || []).map((h) => h.nombre).join(' / ')

  return (
    <>
      {/* Estilos para impresion - oculta toolbar y nav, optimiza tamaños */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
          .programa-imprimible {
            background: #000 !important;
            color: white;
            padding: 0 !important;
            margin: 0 !important;
            min-height: auto !important;
          }
          .programa-imprimible table { page-break-inside: auto; }
          .programa-imprimible tr { page-break-inside: avoid; }
          @page { margin: 1cm; size: A4 portrait; }
        }
      `}</style>

      {/* Toolbar (oculta al imprimir) */}
      <div className="no-print bg-slate-50 border-b border-slate-200 px-4 py-3 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto flex flex-wrap items-end gap-3 justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Volver
            </Button>
            <div>
              <Label htmlFor="horaInicio" className="text-xs">Hora de inicio</Label>
              <Input
                id="horaInicio"
                type="time"
                value={horaInicio}
                onChange={(e) => setHoraInicio(e.target.value)}
                className="w-32"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button onClick={handleCompartir} disabled={generando}>
              {generando ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Share2 className="h-4 w-4 mr-1" />
              )}
              {generando ? 'Generando...' : 'Compartir imagen'}
            </Button>
            <Button variant="outline" onClick={handleImprimir}>
              <Printer className="h-4 w-4 mr-1" />
              Imprimir / PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Vista del flyer */}
      <div className="programa-imprimible bg-black text-white min-h-screen px-4 py-6 sm:px-8 sm:py-10" ref={contentRef}>
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-center gap-4 mb-1">
            {orgLogo && (
              <img
                src={orgLogo}
                alt={org?.nombre || 'Logo'}
                className="h-16 w-16 object-contain"
              />
            )}
            <h1 className="text-2xl sm:text-3xl font-bold tracking-wide uppercase text-center">
              {org?.nombre || 'CLUB'} <span className="text-slate-400">- TEMPORADA {new Date().getFullYear()}</span>
            </h1>
          </div>

          {/* Titulo del programa */}
          <h2 className="text-xl sm:text-2xl font-bold text-center bg-white text-black py-2 my-4 px-4">
            {programa.titulo?.toUpperCase()}
            {enfoque && (
              <span className="text-slate-600"> · {enfoque.toUpperCase()}</span>
            )}
          </h2>

          {!tieneSecciones ? (
            <div className="bg-slate-900 px-6 py-12 text-center text-slate-300">
              Este programa no tiene secciones cronometradas. Edita el programa
              y agrega secciones para poder imprimirlo en formato timetable.
            </div>
          ) : (
            <>
              {/* Tabla */}
              <table className="w-full border-collapse text-sm sm:text-base">
                <thead>
                  <tr className="bg-slate-900 border-b-2 border-slate-700">
                    <th className="text-left px-3 py-2.5 font-bold tracking-wider w-32">TIMETABLE</th>
                    <th className="text-left px-3 py-2.5 font-bold tracking-wider w-1/3">ACTIVIDAD</th>
                    <th className="text-left px-3 py-2.5 font-bold tracking-wider">DETALLES DEL ENTRENAMIENTO</th>
                  </tr>
                </thead>
                <tbody>
                  {timetable.map((row, idx) => (
                    <tr
                      key={idx}
                      className={idx % 2 === 0 ? 'bg-slate-800' : 'bg-slate-900'}
                    >
                      <td className="px-3 py-2.5 font-mono align-top whitespace-nowrap">
                        {row.timetable}
                      </td>
                      <td className="px-3 py-2.5 font-semibold align-top">
                        {row.nombre}
                        <div className="text-[10px] text-slate-400 font-normal uppercase mt-0.5">
                          {getSeccionTipoLabel(row.tipo)} · {row.duracion_min} min
                        </div>
                      </td>
                      <td className="px-3 py-2.5 align-top text-slate-200">
                        {row.descripcion || <span className="text-slate-500 italic">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Foco / descripcion general */}
              {programa.descripcion && (
                <div className="mt-6 px-4 py-3 border-l-4 border-white bg-slate-900">
                  <p className="font-bold tracking-wider mb-2">FOCO DEL ENTRENAMIENTO:</p>
                  <p className="text-sm text-slate-200 whitespace-pre-line">
                    {programa.descripcion}
                  </p>
                </div>
              )}

              {/* Footer con duracion total */}
              <div className="mt-4 text-right text-xs text-slate-400">
                Duracion total: {duracionTotal} · {programa.secciones.length} secciones
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default ProgramaImprimibleView
